import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Cases from "@/pages/Cases";
import CaseDetail from "@/pages/CaseDetail";
import Inventory from "@/pages/Inventory";
import PatientHistory from "@/pages/PatientHistory";
import SendOuts from "@/pages/SendOuts";
import BillingWorklist from "@/pages/BillingWorklist";
import Statistics from "@/pages/Statistics";
import Audit from "@/pages/Audit";
import Team from "@/pages/Team";
import ImportData from "@/pages/ImportData";
import HistologyRequests from "@/pages/HistologyRequests";
import CaseReport from "@/pages/CaseReport";
import Templates from "@/pages/Templates";
import BulkSlides from "@/pages/BulkSlides";
import MyWorklist from "@/pages/MyWorklist";
import Hub from "@/pages/Hub";
import TechWorklist from "@/pages/TechWorklist";
import Documents from "@/pages/Documents";
import DocumentDetail from "@/pages/DocumentDetail";
import TrainingMatrix from "@/pages/TrainingMatrix";
import Reagents from "@/pages/Reagents";
import ReagentDetail from "@/pages/ReagentDetail";
import Equipment from "@/pages/Equipment";
import EquipmentDetail from "@/pages/EquipmentDetail";
import TemperatureTrends from "@/pages/TemperatureTrends";
import Readiness from "@/pages/accreditation/Readiness";
import CapChecklist from "@/pages/accreditation/Checklist";
import ProficiencyTesting from "@/pages/accreditation/ProficiencyTesting";
import Nonconformance from "@/pages/accreditation/Nonconformance";
import SpecimenRejections from "@/pages/accreditation/SpecimenRejections";
import FrozenSections from "@/pages/accreditation/FrozenSections";
import ReportDistribution from "@/pages/accreditation/ReportDistribution";
import Consults from "@/pages/accreditation/Consults";
import Retention from "@/pages/accreditation/Retention";
import Account from "@/pages/Account";
import Interop from "@/pages/Interop";
import Transcription from "@/pages/Transcription";
import CannedTexts from "@/pages/CannedTexts";
import ScanMode from "@/pages/ScanMode";
import Assistant from "@/pages/Assistant";
import TranscriptionEditor from "@/pages/TranscriptionEditor";
import Landing from "@/pages/Landing";
import DemoRequests from "@/pages/DemoRequests";
import Archive from "@/pages/Archive";
import LabLync from "@/pages/LabLync";
import { PathLyncProvider } from "@/pathlync/PathLyncAuth";
import PathLyncAuthPage from "@/pages/pathlync/PathLyncAuthPage";
import PathLyncDashboard from "@/pages/pathlync/PathLyncDashboard";
import ConsultDetail from "@/pages/pathlync/ConsultDetail";

const Protected = ({ children, bare = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-sm text-slate-500">Loading PathQity…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout bare={bare}>{children}</Layout>;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Landing />} />
            <Route path="/pathtel" element={<Navigate to="/pathlync" replace />} />
            <Route path="/pathlync" element={<PathLyncProvider><Outlet /></PathLyncProvider>}>
              <Route index element={<PathLyncAuthPage />} />
              <Route path="dashboard" element={<PathLyncDashboard />} />
              <Route path="consults/:id" element={<ConsultDetail />} />
            </Route>
            <Route path="/demo-requests" element={<Protected><DemoRequests /></Protected>} />
            <Route path="/archive" element={<Protected><Archive /></Protected>} />
            <Route path="/lablync" element={<Protected><LabLync /></Protected>} />
            <Route path="/hub" element={<Protected bare><Hub /></Protected>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/hub" element={<Protected bare><Hub /></Protected>} />
            <Route path="/equipment" element={<Protected bare><Equipment /></Protected>} />
            <Route path="/equipment/temperatures" element={<Protected bare><TemperatureTrends /></Protected>} />
            <Route path="/equipment/:id" element={<Protected bare><EquipmentDetail /></Protected>} />
            <Route path="/reagents" element={<Protected bare><Reagents /></Protected>} />
            <Route path="/reagents/:id" element={<Protected bare><ReagentDetail /></Protected>} />
            <Route path="/accreditation" element={<Protected bare><Readiness /></Protected>} />
            <Route path="/accreditation/checklist" element={<Protected bare><CapChecklist /></Protected>} />
            <Route path="/accreditation/pt" element={<Protected bare><ProficiencyTesting /></Protected>} />
            <Route path="/accreditation/nonconformance" element={<Protected bare><Nonconformance /></Protected>} />
            <Route path="/accreditation/rejections" element={<Protected bare><SpecimenRejections /></Protected>} />
            <Route path="/accreditation/frozen" element={<Protected bare><FrozenSections /></Protected>} />
            <Route path="/accreditation/distribution" element={<Protected bare><ReportDistribution /></Protected>} />
            <Route path="/accreditation/consults" element={<Protected bare><Consults /></Protected>} />
            <Route path="/accreditation/retention" element={<Protected bare><Retention /></Protected>} />
            <Route path="/documents" element={<Protected bare><Documents /></Protected>} />
            <Route path="/documents/matrix" element={<Protected bare><TrainingMatrix /></Protected>} />
            <Route path="/documents/:id" element={<Protected bare><DocumentDetail /></Protected>} />
            <Route path="/tech-worklist" element={<Protected><TechWorklist /></Protected>} />
            <Route path="/my-worklist" element={<Protected><MyWorklist /></Protected>} />
            <Route path="/cases" element={<Protected><Cases /></Protected>} />
            <Route path="/cases/:id" element={<Protected><CaseDetail /></Protected>} />
            <Route path="/cases/:id/report" element={<Protected><CaseReport /></Protected>} />
            <Route path="/patients" element={<Protected><PatientHistory /></Protected>} />
            <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
            <Route path="/sendouts" element={<Protected><SendOuts /></Protected>} />
            <Route path="/histology-requests" element={<Protected><HistologyRequests /></Protected>} />
            <Route path="/recuts" element={<Navigate to="/histology-requests" replace />} />
            <Route path="/bulk-slides" element={<Protected><BulkSlides /></Protected>} />
            <Route path="/templates" element={<Protected><Templates /></Protected>} />
            <Route path="/worklist" element={<Protected><BillingWorklist /></Protected>} />
            <Route path="/statistics" element={<Protected><Statistics /></Protected>} />
            <Route path="/compliance" element={<Navigate to="/accreditation" replace />} />
            <Route path="/audit" element={<Protected><Audit /></Protected>} />
            <Route path="/team" element={<Protected><Team /></Protected>} />
            <Route path="/account" element={<Protected><Account /></Protected>} />
            <Route path="/interop" element={<Protected><Interop /></Protected>} />
            <Route path="/transcription" element={<Protected><Transcription /></Protected>} />
            <Route path="/transcription/:id" element={<Protected><TranscriptionEditor /></Protected>} />
            <Route path="/canned-texts" element={<Protected><CannedTexts /></Protected>} />
            <Route path="/scan" element={<Protected bare><ScanMode /></Protected>} />
            <Route path="/assistant" element={<Protected><Assistant /></Protected>} />
            <Route path="/import" element={<Protected><ImportData /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
